import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { notificationService } from "../services/dataService";
import { AppNotification, NotificationPreference } from "../types";
import { usePermissions } from "../context/PermissionContext";
import PageLoader from "../components/PageLoader";
import { NOTIF_ICON, relTime } from "../components/NotificationBell";
import "./Receivables.css";
import "./Purchasing.css";
import "../components/NotificationBell.css";

/** Bildirim merkezi: liste (tümü / okunmamış) + tür bazında kanal tercihleri. */
const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const { has } = usePermissions();
  const [tab, setTab] = useState<"list" | "prefs">("list");
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [prefs, setPrefs] = useState<NotificationPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [list, p] = await Promise.all([notificationService.list(unreadOnly, 200), notificationService.preferences()]);
      setItems(list); setPrefs(p);
    } catch (e: any) { setError(e?.response?.data?.message || "Bildirimler yüklenemedi."); }
    finally { setLoading(false); }
  }, [unreadOnly]);

  useEffect(() => { load(); }, [load]);

  const open = async (n: AppNotification) => {
    if (!n.isRead) {
      try { await notificationService.markRead(n.notificationId); } catch { /* sessiz */ }
      setItems((prev) => prev.map((x) => (x.notificationId === n.notificationId ? { ...x, isRead: true } : x)));
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try { const m = await notificationService.markAllRead(); setInfo(m > 0 ? `${m} bildirim okundu işaretlendi.` : "Okunmamış bildirim yok."); await load(); }
    catch { setError("İşaretlenemedi."); }
  };

  const markOne = async (e: React.MouseEvent, n: AppNotification) => {
    e.stopPropagation();
    try { await notificationService.markRead(n.notificationId); } catch { return; }
    setItems((prev) => prev.map((x) => (x.notificationId === n.notificationId ? { ...x, isRead: true } : x)));
  };

  const removeOne = async (e: React.MouseEvent, n: AppNotification) => {
    e.stopPropagation();
    try { await notificationService.remove(n.notificationId); } catch { setError("Bildirim silinemedi."); return; }
    setItems((prev) => prev.filter((x) => x.notificationId !== n.notificationId));
  };

  const clearRead = async () => {
    if (!window.confirm("Okunmuş bildirimlerin tümü silinsin mi?")) return;
    try { const r = await notificationService.clearRead(); setInfo(r > 0 ? `${r} bildirim silindi.` : "Silinecek okunmuş bildirim yok."); await load(); }
    catch { setError("Temizlenemedi."); }
  };

  const togglePref = (type: NotificationPreference["type"], key: "inApp" | "email") =>
    setPrefs((prev) => prev.map((p) => (p.type === type ? { ...p, [key]: !p[key] } : p)));

  const savePrefs = async () => {
    setSaving(true); setError(""); setInfo("");
    try { await notificationService.savePreferences(prefs.map((p) => ({ type: p.type, inApp: p.inApp, email: p.email }))); setInfo("Tercihler kaydedildi."); }
    catch { setError("Tercihler kaydedilemedi."); }
    finally { setSaving(false); }
  };

  const runScan = async () => {
    setSaving(true); setError(""); setInfo("");
    try {
      const r = await notificationService.scan(false);
      setInfo(`Tarama tamamlandı: ${r.total} yeni bildirim (termin ${r.deadlineSoon + r.overdue}, stok ${r.stockLow}, cari ${r.receivableOverdue}, satın alma ${r.purchaseOverdue}, sevkiyat ${r.shipmentUndelivered}).`);
      await load();
    } catch (e: any) { setError(e?.response?.data?.message || "Tarama çalıştırılamadı."); }
    finally { setSaving(false); }
  };

  if (loading && items.length === 0 && prefs.length === 0) return <PageLoader message="Bildirimler yükleniyor..." />;

  const unread = items.filter((i) => !i.isRead).length;

  return (
    <div className="rc-page">
      <div className="rc-head">
        <div>
          <h1>Bildirimler</h1>
          <p>Termin, stok, atama, belge ve tahsilat uyarıları. Acil olanlar anında, diğerleri sabah özetiyle e-postaya düşer.</p>
        </div>
        <div className="rc-head-actions">
          {tab === "list" && <button className="rc-btn" onClick={markAll} disabled={unread === 0}>Tümünü okundu işaretle</button>}
          {tab === "list" && <button className="rc-btn danger-ghost" onClick={clearRead} disabled={items.length === 0 || items.every((i) => !i.isRead)}>Okunanları temizle</button>}
          {tab === "prefs" && <button className="rc-btn primary" onClick={savePrefs} disabled={saving}>{saving ? "Kaydediliyor…" : "Tercihleri Kaydet"}</button>}
          {has("settings:update") && <button className="rc-btn" onClick={runScan} disabled={saving} title="Zamana bağlı bildirimleri şimdi üret (normalde her sabah 08:00)">Taramayı Çalıştır</button>}
          <button className="rc-btn" onClick={load} disabled={loading}>Yenile</button>
        </div>
      </div>

      {error && <div className="ic-alert error" style={{ marginBottom: "1rem" }}>{error}</div>}
      {info && <div className="ic-alert warn" style={{ marginBottom: "1rem" }}>{info}</div>}

      <div className="pu-tabs">
        <button className={`pu-tab ${tab === "list" ? "active" : ""}`} onClick={() => setTab("list")}>Bildirimler{unread > 0 ? ` (${unread})` : ""}</button>
        <button className={`pu-tab ${tab === "prefs" ? "active" : ""}`} onClick={() => setTab("prefs")}>Tercihler</button>
      </div>

      {tab === "list" ? (
        <>
          <div className="rc-toolbar">
            <label className="rc-check"><input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} /> Yalnız okunmamışlar</label>
          </div>
          <div className="rc-card np-list" style={{ padding: 0, overflow: "hidden" }}>
            {items.length === 0 ? (
              <div className="rc-empty"><strong>{unreadOnly ? "Okunmamış bildirim yok" : "Henüz bildirim yok"}</strong>Termin, stok ve belge olayları burada listelenir.</div>
            ) : items.map((n) => (
              <div key={n.notificationId} className={`nb-item ${n.isRead ? "" : "unread"} t${n.type}`} onClick={() => open(n)} role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") open(n); }}>
                <span className="nb-ico" aria-hidden="true">{NOTIF_ICON[n.type] || "•"}</span>
                <span className="nb-body">
                  <span className="nb-item-title">{n.title}</span>
                  {n.message && <span className="nb-item-msg">{n.message}</span>}
                  <span className="nb-item-meta">{n.typeName} · {relTime(n.createdAt)} · {new Date(n.createdAt).toLocaleString("tr-TR")}</span>
                  <span className="np-actions">
                    {!n.isRead && <button className="np-act" onClick={(e) => markOne(e, n)}>Okundu işaretle</button>}
                    <button className="np-act danger" onClick={(e) => removeOne(e, n)}>Sil</button>
                  </span>
                </span>
                {!n.isRead && <span className="nb-dot" aria-hidden="true" />}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="rc-card">
          <div className="rc-card-head"><div><h2>Kanal tercihleri</h2><p>Uygulama içi zil ve e-posta ayrı ayrı kapatılabilir. Yetkiniz olmayan türler soluk görünür; size düşmez.</p></div></div>
          <div className="np-prefs"><table>
            <thead><tr><th>Bildirim türü</th><th className="c">Uygulama içi</th><th className="c">E-posta</th></tr></thead>
            <tbody>
              {prefs.map((p) => (
                <tr key={p.type} className={p.applicable ? "" : "na"}>
                  <td>
                    <div className="np-name">{p.name}<span className={`np-tag ${p.instant ? "" : "digest"}`}>{p.instant ? "Anlık" : "Sabah özeti"}</span></div>
                    <div className="np-desc">{p.description}</div>
                  </td>
                  <td className="c"><input type="checkbox" checked={p.inApp} onChange={() => togglePref(p.type, "inApp")} aria-label={`${p.name} uygulama içi`} /></td>
                  <td className="c"><input type="checkbox" checked={p.email} onChange={() => togglePref(p.type, "email")} aria-label={`${p.name} e-posta`} /></td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <div className="rc-hint" style={{ marginTop: "0.75rem" }}>Özet e-postası her sabah 08:00'de, o güne kadar biriken ve e-postası gönderilmemiş bildirimleri tek postada gönderir.</div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
